import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bell, BellOff, Check, Settings, Trash2 } from "lucide-react";
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  showLocalNotification,
  NotificationTypes,
  createNotificationContent,
} from "@/utils/notifications";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

export default function NotificationBell({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  const [permission, setPermission] = useState('default');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check current permission status
    setPermission(getNotificationPermission());
    
    // Register service worker on mount
    registerServiceWorker();
    
    // Check if user has enabled notifications
    const savedPreference = localStorage.getItem('pushNotificationsEnabled');
    setIsEnabled(savedPreference === 'true');
    
    // Fetch in-app notifications
    if (user) {
      fetchNotifications();
      // Also generate automatic reminders (runs once per session)
      generateReminders();
      
      // Polling: Check for new notifications every 30 seconds
      const pollInterval = setInterval(() => {
        fetchNotifications(true); // silent fetch
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [user]);

  const generateReminders = async () => {
    try {
      // Only generate reminders once per session
      const lastGenerated = sessionStorage.getItem('remindersGenerated');
      if (lastGenerated) return;
      
      await fetch(`${API}/notifications/generate-reminders`, {
        method: "POST",
        credentials: "include",
      });
      
      sessionStorage.setItem('remindersGenerated', 'true');
      // Refresh notifications after generating
      setTimeout(fetchNotifications, 1000);
    } catch (error) {
      console.error('Error generating reminders:', error);
    }
  };

  const fetchNotifications = async (silent = false) => {
    try {
      const response = await fetch(`${API}/notifications`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        const newUnreadCount = data.filter(n => !n.is_read).length;
        
        // Show toast only if truly new notifications arrived (not on every poll)
        // Store last notified count in session to prevent repeated toasts
        const lastNotifiedCount = parseInt(sessionStorage.getItem('lastNotifiedCount') || '0');
        if (silent && newUnreadCount > lastNotifiedCount && newUnreadCount > unreadCount) {
          toast.info(`${newUnreadCount - unreadCount} yeni bildirim var!`, { duration: 3000 });
          sessionStorage.setItem('lastNotifiedCount', String(newUnreadCount));
        }
        
        setNotifications(data);
        setUnreadCount(newUnreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    try {
      // Request permission
      const result = await requestNotificationPermission();
      setPermission(result);
      
      if (result === 'granted') {
        // Subscribe to push
        const subscription = await subscribeToPush();
        
        if (subscription) {
          // Save subscription to backend
          await fetch(`${API}/notifications/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              subscription: subscription.toJSON()
            })
          });
          
          setIsEnabled(true);
          localStorage.setItem('pushNotificationsEnabled', 'true');
          toast.success('Bildirimler etkinleştirildi!');
          
          // Show test notification
          await showLocalNotification('Bildirimler Aktif', {
            body: 'Artık önemli güncellemelerden haberdar olacaksınız.',
            tag: 'welcome'
          });
        }
      } else if (result === 'denied') {
        toast.error('Bildirim izni reddedildi. Tarayıcı ayarlarından etkinleştirebilirsiniz.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Bildirimler etkinleştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    try {
      await unsubscribeFromPush();
      
      // Remove subscription from backend
      await fetch(`${API}/notifications/unsubscribe`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setIsEnabled(false);
      localStorage.setItem('pushNotificationsEnabled', 'false');
      toast.success('Bildirimler devre dışı bırakıldı');
    } catch (error) {
      console.error('Error disabling notifications:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reservation_confirmed': return '✅';
      case 'reservation_cancelled': return '❌';
      case 'reservation_reminder': return '⏰';
      case 'payment_received': return '💳';
      case 'payment_debt': return '💰';
      case 'package_expiring': return '📦';
      case 'low_sessions': return '⚠️';
      case 'new_message': return '💬';
      case 'qr_scanned': return '🎫';
      case 'business_rejected': return '🚫';
      case 'business_approved': return '🎉';
      default: return '🔔';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Az önce';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} saat önce`;
    return `${Math.floor(diff / 86400000)} gün önce`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          {isEnabled ? (
            <Bell className="w-5 h-5 text-grey" />
          ) : (
            <BellOff className="w-5 h-5 text-grey" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t("common:notifications")}</span>
          {notifications.length > 0 && unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-auto py-1"
              onClick={handleMarkAllAsRead}
            >
              <Check className="w-3 h-3 mr-1" />
              Tümünü Oku
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Notification Toggle */}
        {isPushSupported() && (
          <>
            <div className="px-2 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-grey" />
                <span className="text-sm">Push Bildirimleri</span>
              </div>
              <Switch
                checked={isEnabled && permission === 'granted'}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleEnableNotifications();
                  } else {
                    handleDisableNotifications();
                  }
                }}
                disabled={loading || permission === 'denied'}
              />
            </div>
            {permission === 'denied' && (
              <p className="px-2 text-xs text-red-500 mb-2">
                Bildirimler tarayıcı tarafından engellendi
              </p>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Notifications List */}
        <div className="max-h-64 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.slice(0, 10).map((notification) => (
              <DropdownMenuItem
                key={notification.notification_id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  !notification.is_read ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleMarkAsRead(notification.notification_id)}
              >
                <span className="text-xl">
                  {getNotificationIcon(notification.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.is_read ? 'font-semibold' : ''}`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-grey truncate">
                    {notification.message || notification.body}
                  </p>
                  <p className="text-xs text-grey mt-1">
                    {formatTime(notification.created_at)}
                  </p>
                </div>
                {!notification.is_read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                )}
              </DropdownMenuItem>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-grey">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t("common:henuzBildirimYok")}</p>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
